import { useState } from 'react'

export interface DeleteAccountRequest {
  password: string
}

export interface DeleteAccountResponse {
  message: string
}

export interface UseDeleteAccountResult {
  deleteAccount: (data: DeleteAccountRequest) => Promise<DeleteAccountResponse | null>
  loading: boolean
  error: string | null
}

export function useDeleteAccount(): UseDeleteAccountResult {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const deleteAccount = async (data: DeleteAccountRequest): Promise<DeleteAccountResponse | null> => {
    try {
      setLoading(true)
      setError(null)

      // Get auth token
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('/api/user/account', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          password: data.password
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        throw new Error(errorData.error || `Failed to delete account (${response.status})`)
      }

      const responseData: DeleteAccountResponse = await response.json()
      return responseData
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete account'
      setError(errorMessage)
      console.error('Error deleting account:', err)
      return null
    } finally {
      setLoading(false)
    }
  }

  return {
    deleteAccount,
    loading,
    error
  }
}
